import { Record } from 'immutable';
import { isEmpty, isNull, isString, map, includes, isArray } from 'lodash';

import { LocaleService, UrlService, ConfigsService, WindowProxy } from '@sm360/widget-commons-core-services';
import { REDIRECTIONS } from '@sm360/widget-commons-general';

import { VEHICLE } from 'commons-vehicle/constants/vehicle.js';
import LEAD_TYPES from 'commons-vehicle/Quote/constants/leadTypes.js';
import VehicleModel from 'commons-vehicle/Vehicle/models/VehicleModel.js';
import {
    CUSTOMER_REVIEW,
    CHECKOUT_WITH_DEPOSIT,
    MANAGER_APPROVAL,
    OFFER_FLAGS,
} from 'commons-vehicle/Quote/constants/statuses.js';
import { DATA_TYPES } from 'commons-vehicle/constants/dataTypes.js';

const QuoteRecord = Record({
    availableItems: [],
    revision: '',
    id: '',
    location: '',
    status: 0,
    creationDate: 0,
    modifiedDate: 0,
    expirationDate: 0,
    dealerId: '',
    accountId: '',
    leadType: LEAD_TYPES.APPOINTMENT,
    leadId: '',
    creditCardChargeId: '',
    creditApplicationId: '',
    vehicle: new VehicleModel(),
    vehicleId: '',
    tradeInVehicles: [],
    isValid: true,
    warnings: [],
    errors: [],
    showroomItems: [],
    securityToken: '',
    modified: false,
});

class Quote extends QuoteRecord {
    constructor(vehicle, modelYear) {
        let quoteVehicle = vehicle instanceof VehicleModel ? vehicle : new VehicleModel(vehicle);

        if (modelYear) {
            const configuration = modelYear.getSelectedOptionsPackageConfiguration();

            quoteVehicle = quoteVehicle
                .set('vehicleConditionType', VEHICLE.CONDITIONS.NEW)
                .set('year', modelYear.year)
                .set('make', modelYear.make)
                .set('model', modelYear.model)
                .set('bodyStyleCode', modelYear.bodyStyleCode)
                .set('configuration', configuration)
                .set('purchaseMethods', configuration.purchaseMethods);
        }

        super({ vehicle: quoteVehicle });
    }

    getShowroomQuote() {
        return {
            __revision: this.revision,
            id: String(this.id),
            location: String(this.location) || ConfigsService.getOrganizationUnitLocation().toLowerCase(),
            status: this.status,
            creationDate: this.creationDate,
            modifiedDate: this.modifiedDate,
            expirationDate: this.expirationDate,
            dealerId: this.dealerId,
            accountId: this.accountId,
            leadId: this.leadId,
            creditCardChargeId: this.creditCardChargeId,
            creditApplicationId: this.creditApplicationId,
            purchaseMethod: this.vehicle.purchaseMethods.getSelectedPurchaseMethodValue(),
            pricingSummary: this.getShowroomQuotePricingSummary(),
            financingSummary: this.getShowroomQuoteFinancingSummary(),
            vehicle: this.getShowroomVehicle(this.vehicle),
            vehicleId: this.vehicle.getIsVehicleNew()
                ? String(this.vehicleId)
                : String(this.vehicleId) || String(this.vehicle.vehicleId),
            tradeIns: this.getShowroomTradeIns(),
            securityToken: String(this.securityToken),
            items: map(this.vehicle.purchaseMethods.getAllSelectedOptions(), (option) => option.getShowroomQuoteItem()),
            availableItems: this.availableItems,
        };
    }

    getShowroomQuotePricingSummary() {
        const {
            modelTrim,
            exteriorColour,
            interiorColour,
            options,
            discounts,
            freightPDI,
            otherFees,
            taxAndDuty,
            rebates,
            protections,
            taxes,
        } = this.vehicle.purchaseMethods.getSelectedPurchaseMethodAmounts();

        const retail =
            modelTrim +
            exteriorColour +
            interiorColour +
            options +
            freightPDI +
            taxAndDuty +
            otherFees -
            (discounts + rebates);

        return {
            retail,
            protections,
            taxes,
            discount: discounts,
            rebate: rebates,
            rebateAtDelivery: 0,
            cashDown: this.vehicle.purchaseMethods.getSelectedFinanceCashDown(),
        };
    }

    getShowroomQuoteFinancingSummary() {
        const selectedFinance = this.vehicle.purchaseMethods.getSelectedFinance();
        const selectedFinanceTerm = this.vehicle.purchaseMethods.getSelectedFinanceTerm();
        const calculatedPayments = selectedFinanceTerm ? selectedFinanceTerm.getCalculatedPayments() : '';

        return !selectedFinanceTerm
            ? {
                  paymentsPerYear: 0,
                  term: 0,
                  apr: 0,
                  financedAmount: 0,
                  financedAmountWithTaxes: 0,
                  validationFinancedAmountMinimum: 0,
                  validationFinancedAmountMaximum: 0,
                  residualizingAmount: 0,
                  residualRate: 0,
                  residualAmountTotal: 0,
                  annualKMAllowed: 0,
                  endTermKMCost: 0,
                  dueAtDelivery: 0,
                  payment: 0,
                  paymentTaxes: 0,
                  protectionsPayment: 0,
              }
            : {
                  paymentsPerYear: selectedFinanceTerm.paymentFrequency,
                  term: selectedFinanceTerm.term,
                  apr: selectedFinanceTerm.apr,
                  financedAmount: selectedFinanceTerm.financedAmount,
                  financedAmountWithTaxes: selectedFinanceTerm.financedAmountWithTaxes,
                  validationFinancedAmountMinimum: 0,
                  validationFinancedAmountMaximum: 0,
                  residualizingAmount: 0,
                  residualRate: selectedFinanceTerm.residualRate,
                  residualAmountTotal: selectedFinanceTerm.residualAmount,
                  annualKMAllowed: selectedFinance.annualKMAllowed,
                  endTermKMCost: selectedFinance.endTermKMCost,
                  dueAtDelivery: 0,
                  payment: calculatedPayments.calculatedPaymentBeforeTaxes,
                  paymentTaxes: calculatedPayments.calculatedPaymentTaxes,
                  protectionsPayment: 0,
              };
    }

    getShowroomVehicle(vehicle) {
        return {
            id: vehicle.id,
            vehicleId: String(vehicle.vehicleId),
            catalogVehicleId: String(vehicle.catalogVehicleId),
            newCarTrimId: String(vehicle.newCarTrimId), // V2.5
            vehicleCondition: vehicle.vehicleConditionType,
            vin: vehicle.vin,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            modelTrimCode: vehicle.configuration !== null ? vehicle.configuration.modelTrimCode : vehicle.modelTrimCode,
            configurationID: vehicle.configuration !== null ? vehicle.configuration.id : vehicle.configurationId,
            configurationToken: vehicle.configurationToken || '',
            inventory: {
                stock: vehicle.stockNumber,
            },
        };
    }

    getShowroomTradeInVehicle(tradeInVehicle) {
        return {
            vehicle: this.getShowroomVehicle(tradeInVehicle.vehicle),
            lien: tradeInVehicle.lien,
            amount: tradeInVehicle.amount,
            evaluationQuestion: tradeInVehicle.evaluationQuestion,
        };
    }

    getShowroomTradeIns() {
        const tradeIns = {
            amountSummary: {
                tradeIn: this.vehicle.purchaseMethods.tradeIn,
                lien: this.vehicle.purchaseMethods.lien,
            },
            tradeInVehicles: [
                {
                    vehicle: {
                        vehicleCondition: VEHICLE.CONDITIONS.USED,
                        year: 0,
                        make: '',
                        model: '',
                    },
                    lien: {
                        owner: '',
                        amount: this.vehicle.purchaseMethods.lien,
                    },
                    amount: this.vehicle.purchaseMethods.tradeIn,
                },
            ],
        };

        if (!isEmpty(this.tradeInVehicles)) {
            tradeIns.tradeInVehicles = map(this.tradeInVehicles, (tradeInVehicle) =>
                this.getShowroomTradeInVehicle(tradeInVehicle)
            );
        }

        return tradeIns;
    }

    getDepositAmount() {
        if (!isArray(this.availableItems)) {
            return 0;
        }
        const deposit = this.availableItems.find(
            (item) =>
                item.dataType === DATA_TYPES.FEE.DATA_TYPE_ID && item.typeID === DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT
        );
        return deposit?.amounts?.retail || 0;
    }

    getVehicleRedirectionFromQuote(vdpUrl) {
        const vehicle = this.vehicle;
        const locale = LocaleService.getSimpleLocale();
        const isQuoteInPending = (this.status & CHECKOUT_WITH_DEPOSIT) === CHECKOUT_WITH_DEPOSIT;
        const isInManagerApprovalStatus = this.isQuoteInApproval();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();
        const redirections = REDIRECTIONS[locale];

        let shortUrl = redirections.checkout;
        let vehicleId = vehicle.vehicleId;

        if (!isQuoteInPending) {
            if (vehicle.getIsVehicleNew()) {
                if (isShowroomV25Enabled) {
                    shortUrl = redirections.catalogMarketplace;
                    vehicleId = vehicle.newCarTrimId;
                } else {
                    shortUrl = redirections.buildAndQuote;
                }
            } else {
                shortUrl = vdpUrl && !isNull(vdpUrl) && isString(vdpUrl) ? vdpUrl : redirections.vdp;
            }

            shortUrl = UrlService.replaceParamWithValue(shortUrl, '{make}', vehicle.make);
            shortUrl = UrlService.replaceParamWithValue(shortUrl, '{model}', vehicle.model);
            shortUrl = UrlService.replaceParamWithValue(shortUrl, '{year}', vehicle.year);
            shortUrl = UrlService.replaceParamWithValue(shortUrl, '{vehicleId}', vehicleId);
            shortUrl = UrlService.replaceParamWithValue(shortUrl, '{bodyStyle}', vehicle.bodyStyleCode);
            shortUrl = shortUrl.toLowerCase().replace(/\s/g, '-');
        }

        return !isInManagerApprovalStatus ? this.buildUrl(shortUrl) : null;
    }

    getVDPUrl() {
        const { vehicle } = this;
        const locale = LocaleService.getSimpleLocale();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();
        const redirections = REDIRECTIONS[locale];

        let vdpURL = redirections.vdp;
        let vehicleId = vehicle.vehicleId;
        if (vehicle.getIsVehicleNew()) {
            if (isShowroomV25Enabled) {
                vdpURL = redirections.catalogMarketplace;
                vehicleId = vehicle.newCarTrimId;
            } else {
                vdpURL = redirections.buildAndQuote;
            }
        }

        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{make}', vehicle.make);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{model}', vehicle.model);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{year}', vehicle.year);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{vehicleId}', vehicleId);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{bodyStyle}', vehicle.bodyStyleCode);
        vdpURL = vdpURL.toLowerCase().replace(/\s/g, '-');

        return this.buildUrl(vdpURL);
    }

    isQuoteInPending() {
        const pendingStatus = CUSTOMER_REVIEW | CHECKOUT_WITH_DEPOSIT;
        return (this.status & pendingStatus) === pendingStatus;
    }

    isQuoteInApproval() {
        return (this.status & MANAGER_APPROVAL) === MANAGER_APPROVAL;
    }

    isQuoteInGarage() {
        return !!(this.id && this.accountId !== 'public');
    }

    isDepositDone() {
        return (this.status & OFFER_FLAGS.OFFER_WITH_DEPOSIT) === OFFER_FLAGS.OFFER_WITH_DEPOSIT;
    }

    isDirty() {
        return !!(this.isQuoteInGarage() && this.modified);
    }

    isQuoteVehicleFromInventory() {
        return !!this.vehicleId;
    }

    buildUrl(url, orgUnitId) {
        const { id, securityToken } = this;
        const prefix = !includes(url, '?') ? '?' : '&';

        if (!url) {
            return '';
        }

        let fullUrl = `${url}${prefix}quoteId=${id}`;

        if (orgUnitId) {
            fullUrl += `&orgUnitId=${orgUnitId}`;
        }

        if (securityToken) {
            const windowProxy = WindowProxy.get();
            if (windowProxy && windowProxy.localStorage) {
                windowProxy.localStorage.setItem('securityToken', securityToken);
            }
        }

        return fullUrl;
    }
}

export default Quote;
